import React, { memo, useEffect, useState } from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { ConfirmDialog } from '../../reports/edit/EditCommon';
import { ACTION_BUTTON_SX } from '../../reports/summary/summaryStyles';
import { finishedNote, formatMoment, groupFailures, lockRemainingText, progressPercent, progressText, tablesPreview } from './revenueOps';
import type { OperationJob, OperationState } from './revenueApi';
import type { RevenueAction } from './useRevenue';

type Tone = 'wait' | 'ok' | 'warn';

const TONES: Record<Tone, { background: string; border: string }> = {
  wait: { background: '#fffbeb', border: '#fde68a' },
  ok: { background: '#f0fdf4', border: '#bbf7d0' },
  warn: { background: '#fff7ed', border: '#fed7aa' }
};

const TEXT_BUTTON_SX = { textTransform: 'none', fontWeight: 600, color: '#475569', borderRadius: '8px' } as const;

/** Текущее время, обновляемое раз в секунду, пока active: нужно для обратного отсчета блокировки */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

interface FailureListProps {
  job: OperationJob;
}

/** Таблицы аптек, с которыми операция не справилась, сгруппированные по причине */
const FailureList: React.FC<FailureListProps> = ({ job }) => {
  const result = job.result;
  if (!result) return null;

  if (result.failures && result.failures.length > 0) {
    return (
      <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {groupFailures(result.failures).map((group) => (
          <Box key={group.kind}>
            <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155' }}>
              {group.label}: {group.tables.length}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b', wordBreak: 'break-word' }}>{tablesPreview(group.tables)}</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{group.hint}</Typography>
          </Box>
        ))}
      </Box>
    );
  }

  if (result.failed && result.failed.length > 0) {
    const names = result.failed.map((item) => item.table);
    const total = typeof result.failedCount === 'number' ? result.failedCount : names.length;
    return (
      <Box sx={{ mt: 1.25 }}>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155' }}>Таблицы с ошибкой: {total}</Typography>
        <Typography sx={{ fontSize: '0.8rem', color: '#64748b', wordBreak: 'break-word' }}>{tablesPreview(names)}</Typography>
        <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>Подробности записаны в журнале действий</Typography>
      </Box>
    );
  }
  return null;
};

/** Одна строка итога завершенной операции: что получилось, кроме общего сообщения */
function finishedDetails(job: OperationJob): string {
  const result = job.result;
  if (!result) return '';
  if (job.op === 'recalc') {
    if (typeof result.tables !== 'number') return '';
    const changed = typeof result.changedTables === 'number' ? result.changedTables : 0;
    return `Просмотрено таблиц: ${result.tables}, изменено: ${changed}`;
  }
  if (typeof result.added === 'number' && typeof result.present === 'number') {
    return `Колонка есть в ${result.present} таблицах, добавлено в ${result.added}`;
  }
  return '';
}

interface OperationPanelProps {
  state: OperationState | null;
  pending: RevenueAction | null;
  canRelease: boolean;
  canRepeat: boolean;
  onRepeat: () => Promise<boolean>;
  onDismiss: () => void;
  onRelease: () => Promise<boolean>;
}

const OperationPanel: React.FC<OperationPanelProps> = ({ state, pending, canRelease, canRepeat, onRepeat, onDismiss, onRelease }) => {
  const [confirming, setConfirming] = useState(false);

  const lock = state ? state.lock : null;
  const job = state ? state.job : null;
  const running = !!job && job.status === 'running';
  const now = useNow(!!lock);

  if (!lock && !job) return null;

  const handleRelease = async () => {
    if (await onRelease()) setConfirming(false);
  };

  const releaseButton =
    lock && canRelease ? (
      <Button
        size="small"
        variant="outlined"
        disabled={pending !== null}
        onClick={() => setConfirming(true)}
        sx={{ ...ACTION_BUTTON_SX, flexShrink: 0 }}
      >
        Снять блокировку
      </Button>
    ) : null;

  const lockLine = lock ? (
    <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mt: 0.5 }}>
      Данные аптек закрыты с {formatMoment(lock.since)}, блокировка снимется сама через {lockRemainingText(lock, now)}
    </Typography>
  ) : null;

  let tone: Tone = 'wait';
  let body: React.ReactNode;
  let actions: React.ReactNode = releaseButton;

  if (running && job) {
    const percent = progressPercent(job);
    body = (
      <>
        <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.92rem' }}>
          {job.title}
          {job.label ? ` «${job.label}»` : ''}: {progressText(job)}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.25 }}>
          Идет обслуживание таблиц выручки, работа с данными аптек приостановлена. Запустил: {job.by}, {formatMoment(job.startedAt)}
        </Typography>
        <LinearProgress
          variant={percent === null ? 'indeterminate' : 'determinate'}
          value={percent === null ? undefined : percent}
          sx={{ mt: 1.25, height: 6, borderRadius: 3, backgroundColor: '#e2e8f0', '& .MuiLinearProgress-bar': { backgroundColor: '#2563eb' } }}
        />
        {lockLine}
      </>
    );
  } else if (lock) {
    // Блокировку держит другая операция (возможно, на другом сервере): хода ее выполнения здесь нет
    body = (
      <>
        <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.92rem' }}>Работа с данными аптек приостановлена</Typography>
        <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.25 }}>
          {lock.title}. Запустил: {lock.by}
        </Typography>
        {lockLine}
      </>
    );
  } else if (job) {
    const note = finishedNote(job);
    const details = finishedDetails(job);
    tone = job.status === 'done' ? 'ok' : 'warn';
    body = (
      <>
        <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.92rem' }}>{note.text}</Typography>
        {details && <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.25 }}>{details}</Typography>}
        <FailureList job={job} />
      </>
    );
    actions = (
      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
        {canRepeat && (
          <Button size="small" variant="outlined" disabled={pending !== null} onClick={onRepeat} sx={ACTION_BUTTON_SX}>
            Повторить
          </Button>
        )}
        <Button size="small" onClick={onDismiss} sx={TEXT_BUTTON_SX}>
          Скрыть
        </Button>
      </Box>
    );
  }

  const palette = TONES[tone];
  return (
    <>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          mb: 2,
          p: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          border: `1px solid ${palette.border}`,
          borderRadius: '12px',
          backgroundColor: palette.background
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>{body}</Box>
        {actions}
      </Box>

      <ConfirmDialog
        open={confirming}
        title="Снять блокировку данных аптек"
        confirmText="Снять блокировку"
        confirmColor="error"
        loading={pending === 'unlock'}
        maxWidth="xs"
        onConfirm={handleRelease}
        onClose={() => setConfirming(false)}
      >
        <Typography sx={{ color: '#334155', fontSize: '0.92rem', mb: 1 }}>
          Работа с данными аптек возобновится сразу.
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: '0.86rem' }}>
          Если операция еще идет, она остановится на текущей таблице: в остальные таблицы колонка не добавится или история
          не пересчитается. Снимайте блокировку, только если операция зависла.
        </Typography>
      </ConfirmDialog>
    </>
  );
};

export default memo(OperationPanel);
